import { z } from 'zod';
import { SUPPORT_CONTRACT_MAX_CONTACTS, SUPPORT_CONTRACT_PERMISSION_LEVELS, SUPPORT_CONTRACT_PERMISSIONS, SUPPORT_CONTRACT_STATES, SUPPORT_CONTRACTS_DELETE_ALL_PHRASE, SUPPORT_ROUNDING_SCOPES, SUPPORT_TICKET_COUNT_MODES, SUPPORT_TICKET_PERIODS, } from '../types/support-contracts.js';
/**
 * A grant as the Settings screen saves it. Only known keys are accepted, and
 * each only at a level its kind allows — a typo or a tampered body is
 * rejected rather than stored.
 */
export const SupportContractPermissionsSchema = z
    .record(z.string(), z.string())
    .superRefine((value, ctx) => {
    for (const [key, level] of Object.entries(value)) {
        const def = SUPPORT_CONTRACT_PERMISSIONS.find((p) => p.key === key);
        if (!def) {
            ctx.addIssue({ code: 'custom', path: [key], message: `Unknown permission "${key}"` });
            continue;
        }
        if (!SUPPORT_CONTRACT_PERMISSION_LEVELS[def.kind].includes(level)) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: `"${level}" is not a valid level for "${key}"`,
            });
        }
    }
});
export const SaveSupportContractAccessSchema = z.object({
    permissions: SupportContractPermissionsSchema,
});
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable();
/**
 * Settings are saved in parts — the contact and the rounding switch have
 * different permissions — so every field is optional and only what is sent
 * changes.
 */
/** Settings → Support logging: the rounding rules builder. */
export const SupportRoundingRulesSchema = z
    .object({
    brackets: z
        .array(z.object({
        upToMinutes: z.number().int().min(1).max(24 * 60),
        billMinutes: z.number().int().min(0).max(24 * 60),
    }))
        .max(20),
    repeatMinutes: z.number().int().min(1).max(24 * 60).nullable(),
    graceMinutes: z.number().int().min(0).max(24 * 60),
})
    .refine((r) => new Set(r.brackets.map((b) => b.upToMinutes)).size === r.brackets.length, 'Two brackets end at the same minute')
    .refine((r) => r.repeatMinutes === null || r.graceMinutes < r.repeatMinutes, 'The free minutes must be fewer than the block');
export const SaveSupportContractSettingsSchema = z.object({
    defaultContactName: optionalText(200).optional(),
    defaultContactPhone: optionalText(50).optional(),
    defaultContactEmail: optionalText(200)
        .refine((v) => v === null || z.string().email().safeParse(v).success, 'Enter a valid email address')
        .optional(),
    roundUpHours: z.boolean().optional(),
    ticketCountMode: z.enum(SUPPORT_TICKET_COUNT_MODES).optional(),
    staffOverTicketLimit: z.boolean().optional(),
    staffOverTicketLimitUserIds: z.array(z.string().uuid()).max(500).optional(),
    requireTimeLog: z.boolean().optional(),
    minTicketMinutes: z.number().int().min(1).max(24 * 60).nullable().optional(),
    roundingRules: SupportRoundingRulesSchema.nullable().optional(),
    roundingScope: z.enum(SUPPORT_ROUNDING_SCOPES).optional(),
});
// ─── Contracts ───────────────────────────────────────────────────────────────
/** A calendar date as YYYY-MM-DD, or null to clear it. */
const dateOnly = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format YYYY-MM-DD')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Not a real date')
    .nullable();
const optionalEmail = optionalText(200).refine((v) => v === null || z.string().email().safeParse(v).success, 'Enter a valid email address');
const PersonSchema = z.object({
    name: optionalText(200),
    phone: optionalText(50),
    email: optionalEmail,
});
const ContractFieldsSchema = z.object({
    customerName: z.string().trim().min(1, 'Customer name is required').max(200),
    projectName: optionalText(300),
    otherName: optionalText(300),
    region: optionalText(50),
    contractTypeId: z.string().uuid().nullable(),
    startDate: dateOnly,
    endDate: dateOnly,
    liveDate: dateOnly,
    totalHours: z.number().min(0).max(100_000).nullable(),
    /** Ticket-based contracts. Null: no ticket limit; left out: unchanged / none. */
    totalTickets: z.number().int().min(0).max(1_000_000).nullable().optional(),
    /** What totalTickets is given per (policy §4.1.2). Null or left out: the whole contract. */
    ticketPeriod: z.enum(SUPPORT_TICKET_PERIODS).nullable().optional(),
    state: z.enum(SUPPORT_CONTRACT_STATES),
    notes: optionalText(5000),
    /** The portal user linked as account manager. Null clears the link. */
    accountManagerUserId: z.string().uuid().nullable(),
    accountManager: PersonSchema,
    projectManager: PersonSchema,
    contacts: z.array(PersonSchema).max(SUPPORT_CONTRACT_MAX_CONTACTS),
    /** The service types the contract covers — replaces the whole list. */
    services: z
        .array(z.object({
        serviceTypeId: z.string().uuid(),
        weight: z.number().min(0).max(100).nullable(),
    }))
        .max(100)
        .refine((list) => new Set(list.map((s) => s.serviceTypeId)).size === list.length, 'A service type is listed twice'),
});
const endNotBeforeStart = (v) => !v.startDate || !v.endDate || v.endDate >= v.startDate;
const END_BEFORE_START = { message: 'The end date is before the start date', path: ['endDate'] };
/** New contract: a customer name and anything else the user may set. */
export const CreateSupportContractSchema = ContractFieldsSchema.partial()
    .required({ customerName: true })
    .refine(endNotBeforeStart, END_BEFORE_START);
/** Changes to a contract: only the fields being changed. */
export const UpdateSupportContractSchema = ContractFieldsSchema.partial().refine(endNotBeforeStart, END_BEFORE_START);
// ─── Contract types ──────────────────────────────────────────────────────────
export const CreateSupportContractTypeSchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(100),
    description: optionalText(1000).optional(),
    /** Omitted on create: false — the type has no kind, and the form shows both caps. */
    hoursBased: z.boolean().optional(),
    ticketsBased: z.boolean().optional(),
    manualHandle: z.boolean().optional(),
});
export const UpdateSupportContractTypeSchema = CreateSupportContractTypeSchema.partial();
// ─── Service types ───────────────────────────────────────────────────────────
export const CreateSupportServiceTypeSchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(100),
    description: optionalText(1000).optional(),
    weight: z.number().min(0).max(100).optional(),
});
export const UpdateSupportServiceTypeSchema = CreateSupportServiceTypeSchema.partial();
// ─── Time logs ───────────────────────────────────────────────────────────────
const TimeLogFieldsSchema = z.object({
    ticketRef: optionalText(50),
    workDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format YYYY-MM-DD')
        .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Not a real date'),
    description: z.string().trim().min(1, 'Describe the work').max(2000),
    serviceTypeId: z.string().uuid().nullable(),
    hours: z.number().min(0).max(1000),
});
export const CreateSupportContractTimeLogSchema = TimeLogFieldsSchema.partial({
    ticketRef: true,
    serviceTypeId: true,
});
export const UpdateSupportContractTimeLogSchema = TimeLogFieldsSchema.partial();
/**
 * Time logged from inside a ticket: the ticket number is filled in for you,
 * and the line goes to the ticket's own contract. `serviceTypeId` left out
 * means the ticket's service type.
 */
export const CreateSupportTicketTimeLogSchema = CreateSupportContractTimeLogSchema.omit({
    ticketRef: true,
});
/** Link a ticket to a contract by its contract ID, or mark it as having none. */
export const SupportTicketContractLinkSchema = z.union([
    z.object({ contractCode: z.string().trim().min(1).max(40) }).strict(),
    z.object({ noContract: z.literal(true) }).strict(),
]);
export const SupportTicketServiceTypeSchema = z.object({
    serviceTypeId: z.string().uuid().nullable(),
});
// ─── Excel import (step 6) ───────────────────────────────────────────────────
/**
 * Confirming a contract import: the same file again, plus which of its rows to
 * bring in (by sheet row number). The file is parsed again rather than trusting
 * rows sent back from the browser.
 */
export const SupportContractImportChoiceSchema = z.object({
    rows: z.array(z.number().int().min(1)).max(5000),
});
/**
 * Confirming a time-sheet import: for each tab, where its lines go — an
 * existing contract, a new contract made from the tab, or nowhere — and
 * whether the tab's plan fills the contract's blank hours and end date.
 */
export const SupportTimeSheetImportMappingSchema = z.object({
    sheets: z
        .array(z.object({
        sheetName: z.string().min(1).max(200),
        target: z.union([z.literal('skip'), z.literal('new'), z.string().uuid()]),
        applyPlan: z.boolean(),
    }))
        .max(500),
});
// ─── Danger zone ─────────────────────────────────────────────────────────────
/** Delete every contract: only with the phrase typed exactly. */
export const DeleteAllSupportContractsSchema = z.object({
    confirmation: z.literal(SUPPORT_CONTRACTS_DELETE_ALL_PHRASE, {
        error: `Type ${SUPPORT_CONTRACTS_DELETE_ALL_PHRASE} to confirm`,
    }),
});
// ─── Ticket usage ────────────────────────────────────────────────────────────
/** Count a ticket against a contract by hand: one of its finished tickets, or a free line. */
export const AddSupportContractTicketUseSchema = z.object({
    ticketId: z.string().uuid().nullable().optional(),
    ticketRef: optionalText(40).optional(),
    useDate: dateOnly.unwrap(),
    note: optionalText(2000).optional(),
});
/** "Count this ticket" on one ticket (user-decides counting). */
export const SetSupportTicketCountsSchema = z.object({ counts: z.boolean() });
//# sourceMappingURL=support-contracts.schema.js.map